import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Environment } from '@micro-core/environment';
import { CreditMockStateService } from './credit-mock-state.service';
import {
  MOCK_CREDIT_SUMMARY,
  MOCK_CREDIT_SUBSCRIPTION,
  MOCK_CREDIT_SUBSCRIPTION_NOT_STARTED,
} from './credit-mock.data';

/**
 * When credit mock is enabled via dev menu, intercepts credit API requests
 * and returns mock responses. Does not modify any credit or credit-setup code.
 * In production, always passes requests through.
 *
 * - Non-credit URLs (mobilestate, step, token, etc.) are passed through immediately.
 * - Mock only when the current route is under /credit or credit-setup (dashboard or setup flow).
 *   When on Home or other routes we pass credit requests through so we don't
 *   trigger the main-thread block that leaves other requests pending ~20s.
 * - When mock is on, GET /service-inventory response is patched so credit state is COMPLETE,
 *   so returning to Home does not reset the credit card to INITIAL.
 */
@Injectable()
export class CreditMockInterceptor implements HttpInterceptor {
  private readonly _env = inject(Environment);
  private readonly _creditMockState = inject(CreditMockStateService);
  private readonly _ngZone = inject(NgZone);
  private readonly _router = inject(Router);

  /** Delay (ms) before emitting mock response so other requests can complete first. */
  private static readonly MOCK_DELAY_MS = 100;

  /** Only mock when user is on a credit or credit-setup route; on Home we pass through to avoid blocking. */
  private _isOnCreditRoute(): boolean {
    const url = this._router.url;
    return url.includes('/credit') || url.includes('credit-setup');
  }

  /** True when we should mock as "not started" so InSetupGuard allows navigation to credit-setup. */
  private _isCreditSetupContext(): boolean {
    const url = this._router.url;
    if (url.includes('credit-setup')) return true;
    const nav = this._router.currentNavigation();
    const target = nav?.finalUrl?.toString() ?? '';
    return target.includes('credit-setup');
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this._env.production) {
      return next.handle(req);
    }
    const url = req.url;

    if (
      this._creditMockState.useCreditMock() &&
      req.method === 'GET' &&
      url.includes('service-inventory')
    ) {
      return next.handle(req).pipe(
        map((event) => {
          if (
            event instanceof HttpResponse &&
            event.body &&
            typeof event.body === 'object' &&
            (event.body as { payload?: { items?: Array<{ key?: string; state?: string }> } }).payload?.items
          ) {
            const body = event.body as { payload: { items: Array<{ key: string; state: string; route?: string }> }; [k: string]: unknown };
            let items = body.payload.items.map((item) =>
              item.key === 'credit' ? { ...item, state: 'COMPLETE' as const } : item
            );
            if (!items.some((i) => i.key === 'credit')) {
              items = [...items, { key: 'credit', state: 'COMPLETE' as const }];
            }
            return event.clone({ body: { ...body, payload: { ...body.payload, items } } });
          }
          return event;
        })
      );
    }

    if (!url.includes('credit')) {
      return next.handle(req);
    }
    if (!this._creditMockState.useCreditMock()) {
      return next.handle(req);
    }
    if (!this._isOnCreditRoute() && !this._isCreditSetupContext()) {
      return next.handle(req);
    }

    let response: HttpResponse<unknown>;
    if (req.method === 'GET' && (url.includes('/credit/') || url.includes('/credit?')) && url.includes('filter=summary')) {
      response = new HttpResponse({ status: 200, body: MOCK_CREDIT_SUMMARY });
    } else if (req.method === 'GET' && url.includes('/credit/subscription')) {
      const subscriptionBody = this._isCreditSetupContext()
        ? MOCK_CREDIT_SUBSCRIPTION_NOT_STARTED
        : MOCK_CREDIT_SUBSCRIPTION;
      response = new HttpResponse({ status: 200, body: subscriptionBody });
    } else if (req.method === 'DELETE' && url.includes('/credit/subscription')) {
      response = new HttpResponse({ status: 200, body: {} });
    } else {
      return next.handle(req);
    }

    return new Observable<HttpEvent<unknown>>((subscriber) => {
      let id: ReturnType<typeof setTimeout>;
      this._ngZone.runOutsideAngular(() => {
        id = setTimeout(() => {
          this._ngZone.run(() => {
            subscriber.next(response);
            subscriber.complete();
          });
        }, CreditMockInterceptor.MOCK_DELAY_MS);
      });
      return () => clearTimeout(id!);
    });
  }
}
